import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core'
import { CreateClaimTabInfo } from '../../../interfaces/create-claim-tab-info'
import { ClaimInformation } from 'app/interfaces/claim-information'
import { ApiService, UserInfo } from '../../../services/api.service'
import { TranslateModule, TranslateService } from '@ngx-translate/core'
import { SelectComponent } from '@components/select/select.component'
import { SelectValue } from '../../../interfaces/select-value.interface'
import { FormsModule, ValidationErrors } from '@angular/forms'
import { ClaimWarrantyInfo } from '../../../interfaces/claim.interface'
import { NgClass, NgIf } from '@angular/common'
import { PdfViewerModule } from 'ng2-pdf-viewer'
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser'
import { AlertComponent } from '@components/alert/alert.component'
import { CheckboxComponent } from '@components/checkbox/checkbox.component'

@Component({
  selector: 'app-claim-warranty',
  standalone: true,
    imports: [
        TranslateModule,
        SelectComponent,
        FormsModule,
        NgIf,
        PdfViewerModule,
        AlertComponent,
        NgClass,
        CheckboxComponent
    ],
  templateUrl: './claim-warranty.component.html',
  styleUrl: './claim-warranty.component.scss'
})
export class ClaimWarrantyComponent implements OnInit, OnChanges{
    @Input() userInfo!: UserInfo
    @Input() claimInformation!: ClaimInformation
    @Input() warrantyAgreementOpen!: boolean

    @Output() tabInit = new EventEmitter<CreateClaimTabInfo>()
    @Output() updateClaim = new EventEmitter<ClaimInformation>()
    @Output() onContinueState = new EventEmitter<boolean>()

    availableWarranties: ClaimWarrantyInfo[] = []
    warrantyOptions: SelectValue[] = [
        {
            id: 'no_warranty_selected',
            label: this.translate.instant('create-claim.warranty.no_warranty_selected'),
            value: 'no_warranty_selected'
        }
    ]
    noAvailableWarranty: SelectValue = {
        id: 'no_warranty_selected',
        label: this.translate.instant('create-claim.warranty.no_warranty_available'),
        value: 'no_warranty_selected',
    }
    selectedWarranty: SelectValue | null = null
    isWarrantySelectorDisabled = false
    isExistingWarranty = false

    // Form validation
    formErrors = {
        emailErrors: false,
    }

    isWarrantyAgreementSigned = false;
    isWarrantyAgreementLoaded = false;
    warrantyAgreementUrl: SafeResourceUrl = ''

    constructor(
        private translate: TranslateService,
        private api: ApiService,
        public sanitizer: DomSanitizer,
        ) {
    }

    ngOnInit() {
        if(this.claimInformation.warrantyData.id){
            this.isExistingWarranty = true
        }
        this.updateNextTab()

        this.fetchWarranties()
        this.fetchWarrantyAgreement()

        this.onClaimWarrantyChange()
    }


    ngOnChanges(changes: SimpleChanges) {
        if(changes['warrantyAgreementOpen']) {
            this.updateNextTab()
            this.onClaimWarrantyChange()
        }
    }

    private fetchWarranties() {
        this.api.getAvailableWarrantiesForDraft(this.claimInformation.claimInfo.id!).subscribe((response) => {
            if(!response){
                this.isWarrantySelectorDisabled = true
                this.warrantyOptions = [this.noAvailableWarranty]
                this.selectedWarranty = this.warrantyOptions[0]
            } else {
                this.availableWarranties = response
                console.log(this.availableWarranties)
                this.availableWarranties.forEach(eachWarranty => {
                    const warrantyOption = {
                        id: eachWarranty.id!,
                        label: eachWarranty.associatedWarranty + ' - ' + eachWarranty.homeOwnerFirstName + ' ' + eachWarranty.homeOwnerLastName,
                        value: eachWarranty.id!
                    }
                    if(this.claimInformation.warrantyData.id === warrantyOption.id){
                        this.selectedWarranty = warrantyOption
                    }
                    this.warrantyOptions.push(warrantyOption)
                })
                this.warrantyOptions = [...this.warrantyOptions]
            }
        })
    }

    private fetchWarrantyAgreement() {
        this.api.getContractInfo('warranty-agreement').subscribe({
            next: (response) => {
                this.warrantyAgreementUrl = this.sanitizer.bypassSecurityTrustResourceUrl(response.contractUrl) //NOSONAR
                this.isWarrantyAgreementLoaded = true
                this.isWarrantyAgreementSigned = response.isSigned
                this.claimInformation.warrantyData.isAgreementSigned = response.isSigned

                this.onClaimWarrantyChange()
            }, error: (error) => {
                console.error('Error loading warranty agreement', error);

                this.onClaimWarrantyChange()
            }
        })
    }

    onWarrantySelected($event: SelectValue | null) {
        if($event == null){
            return
        }

        this.selectedWarranty = $event

        if($event.id === 'no_warranty_selected'){
            this.isExistingWarranty = false
            this.claimInformation.warrantyData = {}
        } else {
            this.isExistingWarranty = true
            const availableWarranty = this.availableWarranties.filter((eachWarranty) => eachWarranty.id === $event.id)
            if(availableWarranty.length === 1) {
                this.claimInformation.warrantyData = availableWarranty[0]
            }
        }

        this.claimInformation.warrantyData.isAgreementSigned = this.isWarrantyAgreementSigned

        this.updateNextTab()

        this.onClaimWarrantyChange()
    }

    updateNextTab(){
        this.tabInit.emit({
            currentTab: this.warrantyAgreementOpen ? 'warranty-agreement' : 'warranty',
            previousTab: this.warrantyAgreementOpen ? 'warranty' : 'classification',
            nextTab: this.warrantyAgreementOpen || this.isExistingWarranty ? 'documentation' : 'warranty-agreement'
        })

        this.claimInformation.lastModifiedTab = this.warrantyAgreementOpen ? 'warranty-agreement' : 'warranty'
    }

    onClaimWarrantyChange(errors?: ValidationErrors | null) {
        this.updateClaim.emit(this.claimInformation)
        this.onContinueState.emit(!errors && this.canContinue())
    }

    canContinue() {
        return this.warrantyAgreementOpen ? this.canContinueWarrantyAgreement() : this.canContinueWarrantyForm()
    }

    canContinueWarrantyForm(){
        return !!(
            this.claimInformation.warrantyData.id ||
            this.claimInformation.warrantyData.homeOwnerFirstName &&
            this.claimInformation.warrantyData.homeOwnerLastName &&
            this.claimInformation.warrantyData.homeOwnerPhone &&
            this.claimInformation.warrantyData.homeOwnerEmail &&
            !this.formErrors.emailErrors &&
            this.claimInformation.warrantyData.homeOwnerStreet &&
            this.claimInformation.warrantyData.homeOwnerCity &&
            this.claimInformation.warrantyData.homeOwnerPostalCode &&
            this.claimInformation.warrantyData.homeOwnerState);
    }

    canContinueWarrantyAgreement(){
        return  !!(this.claimInformation.warrantyData.isAgreementSigned);
    }

    checkInput(errors: ValidationErrors | null, input: 'emailErrors', clearOnNoError = false ) {
        if(clearOnNoError){
            if(this.formErrors[input]){
                this.formErrors[input] = !!errors
            }
        } else {
            this.formErrors[input] = !!errors
        }
        this.onContinueState.emit(this.canContinue())
    }

    updateWarrantyAgreement($event: boolean) {
        this.claimInformation.warrantyData.isAgreementSigned = $event;
        this.isWarrantyAgreementSigned = $event;
        this.onClaimWarrantyChange()
    }
}
